import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import styled from '@emotion/styled';
import { Button, Chip, Stack, Box, Typography, Divider } from '@mui/material';
import {
  DataGrid as MuiDataGrid,
  GridToolbarContainer,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';

import {
  Delete as DeleteIcon,
  Add as AddIcon,
  // Tune as TuneIcon,
  VisibilityOff as VisibilityOffIcon,
  Block as BlockIcon,
  ToggleOn as ToggleOnIcon,
  FormatListBulleted as FormatListBulletedIcon,
  AttachMoney as AttachMoneyIcon,
  Payment as PaymentIcon,
  Devices as DevicesIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import ConfirmationDialog from '@components/ConfirmationDialog';
// import MaintenanceRulesFilter from './MaintenanceRulesFilter';

import {
  getMaintenanceRuleList,
  deleteMaintenanceRule,
  selectIsLoadingMaintenanceRuleList,
  selectMaintenanceRuleFilteredList,
  selectMaintenanceRuleFilteredStatus,
  selectMaintenanceRuleFilteredLoB,
  selectMaintenanceRuleFilteredPlatform,
  selectMaintenanceRuleFilteredCurrency,
  selectMaintenanceRuleFilteredMethodCode,
  selectMaintenanceRuleFilteredStartDate,
  selectMaintenanceRuleFilteredEndDate,
  selectMaintenanceRuleTotalElements,
  paymentOptionsMaintenanceRulesActions,
} from '../../store/payment-options-maintenance-rules';

import {
  MAINTENANCE_RULES_NEW_ROUTE,
  viewMaintenanceRules,
} from '../../payment-options.breadcrumbs';

import {
  PAGE_DETAILS,
  COLUMN_WIDTH,
  MAINTENANCE_RULE_LIST_COLUMNS,
  MAINTENANCE_RULE_DISPLAY_BEHAVIOR,
} from '../../payment-options.constants';

function ActionToolbar({
  navigate,
  permissions,
  // onFilterClick,
  showDeleteAction,
  onRowDelete,
  filteredStatus,
  onStatusRemoval,
  filteredLoB,
  onLoBRemoval,
  filteredPlatform,
  onPlatformRemoval,
  filteredCurrency,
  onCurrencyRemoval,
  filteredMethodCode,
  onMethodCodeRemoval,

  filteredStartDate,
  filteredEndDate,
  onDateRangeRemoval,
}) {
  return (
    <GridToolbarContainer
      sx={{
        padding: '10px',
        gap: '10px',
        justifyContent: 'flex-end',
      }}>
      <Stack
        flex={1}
        direction="row"
        gap="8px"
        flexWrap="wrap">
        {filteredStatus.map((status) => (
          <Chip
            icon={<ToggleOnIcon />}
            key={status}
            label={status}
            onDelete={() => {
              onStatusRemoval(status);
            }}
          />
        ))}

        {filteredLoB.map((lob) => (
          <Chip
            icon={<FormatListBulletedIcon />}
            key={lob}
            label={lob}
            onDelete={() => {
              onLoBRemoval(lob);
            }}
          />
        ))}

        {filteredPlatform.map((platform) => (
          <Chip
            icon={<DevicesIcon />}
            key={platform}
            label={platform}
            onDelete={() => {
              onPlatformRemoval(platform);
            }}
          />
        ))}

        {filteredCurrency.map((currency) => (
          <Chip
            icon={<AttachMoneyIcon />}
            key={currency}
            label={currency}
            onDelete={() => {
              onCurrencyRemoval(currency);
            }}
          />
        ))}

        {filteredMethodCode.map((methodCode) => (
          <Chip
            icon={<PaymentIcon />}
            key={methodCode}
            label={methodCode}
            onDelete={() => {
              onMethodCodeRemoval(methodCode);
            }}
          />
        ))}

        {(filteredStartDate || filteredEndDate) && (
          <Chip
            icon={<DateRangeIcon />}
            label={`${
              filteredStartDate
                ? dayjs(filteredStartDate).format('DD/MM/YYYY')
                : 'No Start Date'
            } - ${
              filteredEndDate
                ? dayjs(filteredEndDate).format('DD/MM/YYYY')
                : 'No End Date'
            }`}
            onDelete={onDateRangeRemoval}
          />
        )}
      </Stack>
      <Stack
        direction="row"
        gap="10px">
        {showDeleteAction && (
          <>
            <Button
              variant="contained"
              disabled={!permissions?.canDeleteMaintenanceRule}
              onClick={onRowDelete}
              startIcon={<DeleteIcon />}>
              Delete
            </Button>
            <Divider
              orientation="vertical"
              flexItem
            />
          </>
        )}
        {/* <Button
          variant="outlined"
          onClick={onFilterClick}
          startIcon={<TuneIcon />}>
          Filter
        </Button> */}
        <Button
          variant="contained"
          disabled={!permissions?.canCreateMaintenanceRule}
          onClick={() => navigate(MAINTENANCE_RULES_NEW_ROUTE)}
          startIcon={<AddIcon />}>
          Add new rule
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

const DataGrid = styled(MuiDataGrid)`
  & .MuiDataGrid-columnHeader [class~='MuiDataGrid-columnHeaderTitle'] {
    white-space-collapse: break-spaces;
    text-wrap: wrap;
    line-height: normal;
  }

  &
    .MuiDataGrid-columnHeader--alignRight
    [class~='MuiDataGrid-columnHeaderTitle'] {
    text-align: right;
  }

  &
    .MuiDataGrid-columnHeader--alignCenter
    [class~='MuiDataGrid-columnHeaderTitle'] {
    text-align: center;
  }
`;

export function getMultilineCustomColumn(line1, line2) {
  return (
    <Box>
      <Typography
        variant="body1"
        fontWeight="500"
        sx={{ whiteSpace: 'break-spaces' }}>
        {line1}
      </Typography>
      {line2 && <Typography variant="caption">{line2}</Typography>}
    </Box>
  );
}

export function getStatusCustomColumn(isActive) {
  return (
    <Chip
      label={isActive ? 'Enabled' : 'Disabled'}
      color={isActive ? 'success' : 'error'}
      variant="outlined"
    />
  );
}

export function getCurrencyCustomColumn(code) {
  return (
    <Chip
      label={code}
      variant="outlined"
    />
  );
}

export function getDisplayBehaviorCustomColumn(label) {
  let color;
  let icon;
  switch (label) {
    case MAINTENANCE_RULE_DISPLAY_BEHAVIOR.DISABLE:
      color = 'warning';
      icon = <VisibilityOffIcon />;
      break;
    case MAINTENANCE_RULE_DISPLAY_BEHAVIOR.HIDE:
      color = 'error';
      icon = <BlockIcon />;
      break;

    default:
      color = 'info';
      icon = <VisibilityOffIcon />;
      break;
  }

  return <Chip {...{ label, color, icon }} />;
}

function getMaintenanceRuleListCustomColumn(row, value, field) {
  switch (field) {
    case 'active':
      return getStatusCustomColumn(value);

    case 'merchant': {
      const { lobName } = value;
      return getMultilineCustomColumn(lobName);
    }

    case 'platform': {
      const { name = '' } = value;
      return getMultilineCustomColumn(name);
    }

    case 'currency': {
      const { code } = value;
      return getCurrencyCustomColumn(code);
    }

    case 'methodCode': {
      const { methodName, methodCode } = value;
      return getMultilineCustomColumn(methodName, methodCode);
    }

    case 'message': {
      const { message, messageKey } = row;
      return getMultilineCustomColumn(messageKey, message);
    }

    case 'startTime': {
      const { startTime } = row;
      const date = moment(startTime).format('DD/MM/YYYY');
      const time = moment(startTime).format('hh:mm A');

      return getMultilineCustomColumn(date, time);
    }

    case 'endTime': {
      const { endTime } = row;
      const date = moment(endTime).format('DD/MM/YYYY');
      const time = moment(endTime).format('hh:mm A');

      return getMultilineCustomColumn(date, time);
    }

    case 'displayBehavior':
      return getDisplayBehaviorCustomColumn(value);

    case 'createdBy': {
      const { createdBy, createdAt } = row;
      const date = moment(createdAt).format('DD/MM/YYYY, hh:mm A');
      return getMultilineCustomColumn(createdBy, date);
    }

    case 'modifiedBy': {
      const { modifiedBy, modifiedAt } = row;
      const date = moment(modifiedAt).format('DD/MM/YYYY, hh:mm A');
      return getMultilineCustomColumn(modifiedBy, date);
    }

    default:
      return value;
  }
}

function getMaintenanceRuleListColumns() {
  return MAINTENANCE_RULE_LIST_COLUMNS.map(
    ({ width = COLUMN_WIDTH.MD, ...col }) => ({
      ...col,
      width,
      sortable: false,
      renderCell: ({ value, row }) =>
        getMaintenanceRuleListCustomColumn(row, value, col.field),
    }),
  );
}

function MaintenanceRulesList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  // const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [page, setPage] = useState(0);

  const maintenanceRuleList = useSelector(selectMaintenanceRuleFilteredList);
  const totalElements = useSelector(selectMaintenanceRuleTotalElements);
  const isLoadingMaintenanceRuleList = useSelector(
    selectIsLoadingMaintenanceRuleList,
  );

  const filteredStatus = useSelector(selectMaintenanceRuleFilteredStatus);
  const filteredLoB = useSelector(selectMaintenanceRuleFilteredLoB);
  const filteredPlatform = useSelector(selectMaintenanceRuleFilteredPlatform);
  const filteredCurrency = useSelector(selectMaintenanceRuleFilteredCurrency);
  const filteredMethodCode = useSelector(
    selectMaintenanceRuleFilteredMethodCode,
  );
  const filteredStartDate = useSelector(selectMaintenanceRuleFilteredStartDate);
  const filteredEndDate = useSelector(selectMaintenanceRuleFilteredEndDate);

  const showDeleteAction = selectedRows.length !== 0;

  useEffect(() => {
    if (!permissions?.canViewMaintenanceRule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.setBreadcrumbs(viewMaintenanceRules));
  }, [dispatch]);

  // API: Get Maintenance Rule List
  useEffect(() => {
    dispatch(getMaintenanceRuleList({ active: true, page, size: 30 }));
  }, [dispatch, page]);

  // const onFilterClick = () => {
  //   setIsFilterModalVisible(true);
  // };

  const onStatusRemoval = (status) => {
    dispatch(paymentOptionsMaintenanceRulesActions.removeFilterStatus(status));
  };
  const onLoBRemoval = (lob) => {
    dispatch(paymentOptionsMaintenanceRulesActions.removeFilterLoB(lob));
  };
  const onPlatformRemoval = (platform) => {
    dispatch(
      paymentOptionsMaintenanceRulesActions.removeFilterPlatform(platform),
    );
  };
  const onCurrencyRemoval = (currency) => {
    dispatch(
      paymentOptionsMaintenanceRulesActions.removeFilterCurrency(currency),
    );
  };
  const onMethodCodeRemoval = (methodCode) => {
    dispatch(
      paymentOptionsMaintenanceRulesActions.removeFilterMethodCode(methodCode),
    );
  };
  const onDateRangeRemoval = () => {
    dispatch(paymentOptionsMaintenanceRulesActions.removeFilterStartEndDate());
  };

  const onRowDelete = () => {
    setShowDeleteModal({ show: true });
  };

  const yesDeleteHandler = (ids) => {
    dispatch(deleteMaintenanceRule({ ids })).then(
      ({ payload, meta: { requestStatus } }) => {
        if (requestStatus !== 'fulfilled' || !payload) {
          dispatch(
            showErrorMessage(PAGE_DETAILS.MaintenanceRuleList.DeleteFailure),
          );
          return;
        }

        dispatch(
          showSuccessMessage(PAGE_DETAILS.MaintenanceRuleList.DeleteSuccess),
        );
        dispatch(getMaintenanceRuleList({ active: true, page, size: 30 }));
      },
    );
  };

  return (
    <ApplicationPageTemplate
      title={PAGE_DETAILS.MaintenanceRuleList.PageTitle}
      description={PAGE_DETAILS.MaintenanceRuleList.PageDescription}
      pageReady>
      <DataGrid
        autoHeight
        getRowHeight={() => 'auto'}
        density="comfortable"
        checkboxSelection
        loading={isLoadingMaintenanceRuleList}
        rows={maintenanceRuleList}
        paginationMode="server"
        rowCount={totalElements}
        page={page}
        onPageChange={(newPage) => setPage(newPage)}
        rowsPerPageOptions={[30]}
        pageSize={30}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        onSelectionModelChange={setSelectedRows}
        components={{
          Toolbar: () =>
            ActionToolbar({
              navigate,
              permissions,
              // onFilterClick,
              showDeleteAction,
              onRowDelete,
              filteredStatus,
              onStatusRemoval,
              filteredLoB,
              onLoBRemoval,
              filteredPlatform,
              onPlatformRemoval,
              filteredCurrency,
              onCurrencyRemoval,
              filteredMethodCode,
              onMethodCodeRemoval,
              filteredStartDate,
              filteredEndDate,
              onDateRangeRemoval,
            }),
        }}
        getRowId={({ id }) => id}
        columns={getMaintenanceRuleListColumns()}
      />
      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={yesDeleteHandler}
        data={selectedRows}
        title={PAGE_DETAILS.MaintenanceRuleList.DeleteConfirmationTitle}
        message={PAGE_DETAILS.MaintenanceRuleList.DeleteConfirmationMessage}
      />
      {/* <MaintenanceRulesFilter
        isVisible={isFilterModalVisible}
        onClose={setIsFilterModalVisible}
      /> */}
    </ApplicationPageTemplate>
  );
}

export default MaintenanceRulesList;
